import { createContext, ReactNode, useCallback, useContext } from 'react';

import { UseMutationResult } from '@tanstack/react-query';

import {
  useAssignmentValidation,
  useCreateAssignment,
  useCutAssignment,
  useDeleteAssignment,
  useDuplicateAssignment,
  useUpdateAssignment,
} from '../hooks';
import usePlannerStore, { EditAssignmentData } from '../store';
import {
  AssignmentResponse,
  CreateAssignmentWithUIRequest,
  CutAssignmentWithUIRequest,
  DeleteAssignmentWithUIRequest,
  DuplicateAssignmentRequest,
  DuplicateAssignmentUIData,
  DuplicateAssignmentWithUIRequest,
  UpdateAssignmentRequest,
  UpdateAssignmentWithUIRequest,
  ValidateAssignmentRequest,
  ValidateAssignmentResponse,
} from '../types/dto';

// Context value interface
interface AssignmentActionsContextValue {
  // Mutations
  updateMutation: UseMutationResult<
    AssignmentResponse,
    Error,
    UpdateAssignmentWithUIRequest,
    unknown
  >;
  deleteMutation: UseMutationResult<void, Error, DeleteAssignmentWithUIRequest, unknown>;
  cutMutation: UseMutationResult<void, Error, CutAssignmentWithUIRequest, unknown>;
  createMutation: UseMutationResult<
    AssignmentResponse,
    Error,
    CreateAssignmentWithUIRequest,
    unknown
  >;
  duplicateMutation: UseMutationResult<
    AssignmentResponse,
    Error,
    DuplicateAssignmentWithUIRequest,
    unknown
  >;

  // Validation
  validateForUpdate: UseMutationResult<
    ValidateAssignmentResponse,
    Error,
    ValidateAssignmentRequest,
    unknown
  >;
  validateForDuplicate: UseMutationResult<
    ValidateAssignmentResponse,
    Error,
    ValidateAssignmentRequest,
    unknown
  >;

  // Actions
  handleEditAssignment: (data: EditAssignmentData) => void;
  handleDeleteAssignment: (id: number, ui?: { userName?: string; projectName?: string }) => void;
  handleOpenDeleteConfirmation: (
    id: number,
    ui?: { userName?: string; projectName?: string }
  ) => void;
  handleCutAssignment: (
    id: number,
    cutDate: string,
    syncToken: string,
    ui?: { userName?: string; projectName?: string }
  ) => void;
  handleValidateAndUpdate: (
    params: UpdateAssignmentRequest & {
      resetPosition?: () => void;
      ui?: { userName?: string; projectName?: string };
    }
  ) => void;
  handleValidateAndDuplicate: (
    params: DuplicateAssignmentRequest & { ui?: DuplicateAssignmentUIData }
  ) => void;
}

// Create context
const AssignmentActionsContext = createContext<AssignmentActionsContextValue | null>(null);

// Provider props
interface AssignmentActionsProviderProps {
  children: ReactNode;
}

// Provider component
export const AssignmentActionsProvider = ({ children }: AssignmentActionsProviderProps) => {
  const setEditAssignmentData = usePlannerStore(store => store.setEditAssignmentData);
  const setShowOverlapWarning = usePlannerStore(store => store.setShowOverlapWarning);
  const setDeleteAssignmentData = usePlannerStore(store => store.setDeleteAssignmentData);

  // Initialize all mutation hooks once
  const updateMutation = useUpdateAssignment();
  const deleteMutation = useDeleteAssignment();
  const cutMutation = useCutAssignment();
  const createMutation = useCreateAssignment();
  const duplicateMutation = useDuplicateAssignment();

  // Validation hook for update operations (drag/resize)
  const validateForUpdate = useAssignmentValidation({
    onSuccess: (data, variables) => {
      if (data.overlapping) {
        setShowOverlapWarning(true);
      }
    },
  });

  // Validation hook for duplicate operations
  const validateForDuplicate = useAssignmentValidation({
    onSuccess: (data, variables) => {
      if (data.overlapping) {
        setShowOverlapWarning(true);
      }
    },
  });

  // Action handlers
  const handleEditAssignment = useCallback(
    (data: EditAssignmentData) => {
      setEditAssignmentData(data);
    },
    [setEditAssignmentData]
  );

  const handleDeleteAssignment = useCallback(
    (id: number, ui?: { userName?: string; projectName?: string }) => {
      deleteMutation.mutate({ payload: { assignmentId: id }, ui });
    },
    [deleteMutation]
  );

  const handleOpenDeleteConfirmation = useCallback(
    (id: number, ui?: { userName?: string; projectName?: string }) => {
      setDeleteAssignmentData({ id, userName: ui?.userName, projectName: ui?.projectName });
    },
    [setDeleteAssignmentData]
  );

  const handleCutAssignment = useCallback(
    (
      id: number,
      cutDate: string,
      syncToken: string,
      ui?: { userName?: string; projectName?: string }
    ) => {
      cutMutation.mutate({ payload: { assignmentId: id, cutDate, syncToken }, ui });
    },
    [cutMutation]
  );

  const handleValidateAndUpdate = useCallback(
    (
      params: UpdateAssignmentRequest & {
        resetPosition?: () => void;
        userId: number;
        projectId: number;
        startDate: string;
        endDate: string;
        ui?: { userName?: string; projectName?: string };
      }
    ) => {
      const { resetPosition, ui, ...validationParams } = params;

      validateForUpdate.mutate(
        {
          userId: validationParams.userId,
          projectId: validationParams.projectId,
          startDate: validationParams.startDate,
          endDate: validationParams.endDate,
          assignmentId: validationParams.assignmentId,
        },
        {
          onSuccess: data => {
            if (data.overlapping) {
              // Show overlap warning and reset position if provided
              setShowOverlapWarning(true);
              resetPosition?.();
            } else {
              // No overlap, proceed with update
              updateMutation.mutate({ payload: validationParams, ui });
            }
          },
          onError: () => {
            // Reset position on validation error
            resetPosition?.();
          },
        }
      );
    },
    [validateForUpdate, updateMutation, setShowOverlapWarning]
  );

  const handleValidateAndDuplicate = useCallback(
    (params: DuplicateAssignmentRequest & { ui?: DuplicateAssignmentUIData }) => {
      // userId and projectId for overlap validation come from the ui data since
      // the API payload only carries assignmentId, startDate, and endDate.
      validateForDuplicate.mutate(
        {
          userId: params.ui?.userId ?? 0,
          projectId: params.ui?.projectId ?? 0,
          startDate: params.startDate,
          endDate: params.endDate,
          assignmentId: undefined, // No exclusion — this is a new assignment
        },
        {
          onSuccess: data => {
            if (!data.overlapping) {
              // No overlap — call the dedicated duplicate endpoint
              duplicateMutation.mutate({
                payload: {
                  assignmentId: params.assignmentId,
                  startDate: params.startDate,
                  endDate: params.endDate,
                },
                ui: params.ui,
              });
            }
          },
        }
      );
    },
    [validateForDuplicate, duplicateMutation]
  );

  const value: AssignmentActionsContextValue = {
    updateMutation,
    deleteMutation,
    cutMutation,
    createMutation,
    duplicateMutation,
    validateForUpdate,
    validateForDuplicate,
    handleEditAssignment,
    handleDeleteAssignment,
    handleOpenDeleteConfirmation,
    handleCutAssignment,
    handleValidateAndUpdate,
    handleValidateAndDuplicate,
  };

  return (
    <AssignmentActionsContext.Provider value={value}>{children}</AssignmentActionsContext.Provider>
  );
};

// Custom hook to use the context
export const useAssignmentActions = () => {
  const context = useContext(AssignmentActionsContext);
  if (!context) {
    throw new Error('useAssignmentActions must be used within AssignmentActionsProvider');
  }
  return context;
};
